import React, { Component } from 'react'
import PropTypes from 'prop-types'
import DnsLookupButton from './DnsLookupButton'
import OrganizationLookupButton from './OrganizationLookupButton'
import DigitalPropertySplitModal from './OrganizationSplitModal'
import ContactForm from './ContactForm'
import './ContactEnrichment.css'
import { getApiBase } from '../../config'
import {
	markViolationForExport,
	markNoContactInformation,
	createOrganizations,
	updateFirstDateOfPublication,
	updateWaybackUrl,
	updateViolationMarket,
	updateViolationType,
	updateInternalComment,
} from './contactEnrichmentUtils'
import { markets } from '../../lib/markets'
import { dedupStrings } from '../../lib/contactValidation'
import isURL from 'validator/lib/isURL'

const ALLOCATION_NOTE_OPTIONS = [
	'Check market',
	'Check pricing',
	'Swedish Police',
	'Mediahuis',
]

const dateOfPublicationAvailable = ({ market, isRegistered }) => {
	return (market === 'US' && isRegistered) || market === 'DE'
}

class DigitalPropertyContactEnrichmentCard extends Component {
	constructor(props) {
		super(props)

		// Only store UI state, not domain data
		this.state = {
			saving: false,
			saved: false,
			showSplitModal: false, // Track split modal visibility
			isFormValid: false, // Track if contact form is valid
		}
	}

	handleOrganizationFound = (org) => {
		const { organization, onOrganizationChange } = this.props
		if (onOrganizationChange) {
			onOrganizationChange({
				...organization,
				organizationId: org.organizationId,
				companyName: org.companyName || organization.companyName,
				companyNumber: org.companyNumber || organization.companyNumber,
				address: org.address || organization.address,
				postalCode: org.postalCode || organization.postalCode,
				city: org.city || organization.city,
				country: org.country || organization.country,
				counterpartyType: org.counterpartyType || organization.counterpartyType,
				emailAddresses:
					org.emailAddresses && org.emailAddresses.length > 0
						? [...org.emailAddresses]
						: organization.emailAddresses,
				bouncedEmailAddresses: org.bouncedEmailAddresses || [],
				phoneNumbers:
					org.phoneNumbers && org.phoneNumbers.length > 0
						? org.phoneNumbers.map((p) => ({ ...p }))
						: organization.phoneNumbers,
			})
		}
	}

	saveViolationFields = async (violations) => {
		for (const violation of violations) {
			if (violation.violationType) {
				await updateViolationType({
					violationId: violation.violationId,
					violationType: violation.violationType,
				})
			}
			if (violation.market) {
				await updateViolationMarket(violation.violationId, violation.market)
			}
			if (
				dateOfPublicationAvailable({
					market: violation.market,
					isRegistered: violation.registered,
				})
			) {
				await updateFirstDateOfPublication(
					violation.violationId,
					violation.firstDateOfPublication || null,
				)
				await updateWaybackUrl(
					violation.violationId,
					violation.waybackUrl || null,
				)
			} else {
				await updateFirstDateOfPublication(violation.violationId, null)
				await updateWaybackUrl(violation.violationId, null)
			}
			if (violation.internalComment !== undefined) {
				await updateInternalComment(
					violation.violationId,
					violation.internalComment,
				)
			}
		}
	}

	updateViolationField = (violationId, field, value) => {
		const { organization, onOrganizationChange } = this.props
		if (onOrganizationChange) {
			const updatedViolations = organization.violations.map((v) =>
				v.violationId === violationId ? { ...v, [field]: value } : v,
			)
			onOrganizationChange({ ...organization, violations: updatedViolations })
		}
	}

	handleFormValidChange = (isValid) => {
		this.setState({ isFormValid: isValid })
	}

	handleDomainSplitSave = async () => {
		// Close the modal
		this.setState({ showSplitModal: false })

		// Refresh organizations to show the newly created subdomains
		if (this.props.onSave) {
			await this.props.onSave()
		}
	}

	render() {
		const { organization, onSave, onSubmit, index } = this.props
		const o = organization

		const isSaving = this.state.saving
		const isSaved = this.state.saved
		const isFormValid = this.state.isFormValid
		const allViolationsHaveViolationType = o.violations.every(
			(v) => v.violationType,
		)
		const allViolationsHaveMarket = o.violations.every((v) => v.market)
		const allWaybackUrlsValid = o.violations.every(
			(v) => !v.waybackUrl || isURL(v.waybackUrl),
		)
		const isSubmitDisabled =
			isSaving ||
			!isFormValid ||
			!allViolationsHaveViolationType ||
			!allViolationsHaveMarket ||
			!allWaybackUrlsValid

		return (
			<div
				key={o.organizationId || index}
				className='violation-card'
				data-organization-id={o.organizationId}
			>
				{/* Violation Number Badge */}
				{index !== undefined && (
					<div
						style={{
							position: 'absolute',
							top: '12px',
							right: '12px',
							backgroundColor: '#3b82f6',
							color: 'white',
							fontWeight: 'bold',
							fontSize: '14px',
							padding: '6px 12px',
							borderRadius: '6px',
							boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
							zIndex: 10,
						}}
					>
						#{index + 1}
					</div>
				)}
				{/* Header with Organization, Domains, and Violations */}
				<div className='violation-header'>
					{/* Organization Info */}
					<div className='case-id-section'>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<div className='field-label'>Organization</div>
							{o.violations.length > 0 && (
								<button
									onClick={() => this.setState({ showSplitModal: true })}
									style={{
										background: '#3b82f6',
										color: 'white',
										border: 'none',
										borderRadius: '4px',
										padding: '4px 8px',
										fontSize: '12px',
										fontWeight: '600',
										cursor: 'pointer',
										transition: 'all 0.2s ease',
									}}
									onMouseOver={(e) => {
										e.target.style.background = '#2563eb'
									}}
									onMouseOut={(e) => {
										e.target.style.background = '#3b82f6'
									}}
									title='Create new organization'
								>
									New Organization
								</button>
							)}
						</div>
						<div className='case-id-value'>{o.companyName || '—'}</div>

						{/* Domains List */}
						<div style={{ marginTop: '12px' }}>
							<div className='field-label'>
								Digital properties ({o.domains && o.domains.length ? o.domains.length : 0})
							</div>
							{o.domains && o.domains.length > 0 ? (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '4px',
										marginTop: '4px',
									}}
								>
									{o.domains.map((domain) => (
										<div
											key={domain.digitalPropertyId}
											style={{ fontSize: '14px', color: '#374151' }}
										>
											• {domain.domainName}
										</div>
									))}
								</div>
							) : (
								<div style={{ fontSize: '14px', color: '#6b7280' }}>
									No digital properties
								</div>
							)}
						</div>

						{o.domains && o.domains.length > 0 && (
							<div style={{ marginTop: '8px' }}>
								<DnsLookupButton
									violation={{ domain: o.domains[0].domainName }}
									style={{ fontSize: '14px' }}
								/>
							</div>
						)}
						{o.country && (
							<div style={{ marginTop: '4px' }}>
								<OrganizationLookupButton
									country={o.country}
									style={{ fontSize: '14px' }}
								/>
							</div>
						)}
						<a
							href='https://hunter.io/'
							target='_blank'
							rel='noopener noreferrer'
							style={{
								color: '#3b82f6',
								textDecoration: 'underline',
								cursor: 'pointer',
								fontSize: '14px',
								marginTop: '4px',
								display: 'inline-block',
							}}
						>
							Email lookup
						</a>
					</div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							width: '100%',
						}}
					>
						{o.violations.map((violation) => (
							<div key={violation.violationId}>
								{violation.scanLocation === 'manual' && (
									<div
										style={{
											background: '#fde2e1',
											border: '2px solid #d32f2f',
											borderRadius: '4px',
											padding: '6px 10px',
											marginBottom: '6px',
											color: '#b71c1c',
											fontWeight: 'bold',
											fontSize: '13px',
										}}
									>
										⚠ MANUAL SUBMISSION — review with extra care
									</div>
								)}
								<div
									style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}
								>
									{/* Case ID */}
									<div style={{ flex: 1 }}>
										<div className='field-label'>Case ID</div>
										<div className='case-id-value'>{violation.caseId}</div>
										{violation.violationId && (
											<p
												style={{
													fontSize: '0.85em',
													fontFamily: 'monospace',
													color: 'rgba(0,0,0,0.6)',
													margin: '2px 0',
												}}
											>
												Violation ID: {violation.violationId}
											</p>
										)}
										{violation.casePrepOutcome &&
											(() => {
												const casePrepBaseUrl =
													process.env.REACT_APP_CASE_PREP_URL ||
													'https://prepare.copyrightagent.com'
												const url = `${casePrepBaseUrl}/cases/lookup?violationId=${encodeURIComponent(violation.violationId)}`
												return (
													<p style={{ margin: '2px 0' }}>
														<a
															href={url}
															target='_blank'
															rel='noopener noreferrer'
															style={{
																color: 'blue',
																textDecoration: 'underline',
																fontSize: '0.9em',
															}}
														>
															Case prep: {violation.casePrepOutcome}
														</a>
														{violation.casePrepOccurredAt && (
															<span
																style={{
																	fontSize: '0.8em',
																	opacity: 0.6,
																	marginLeft: '6px',
																}}
															>
																(
																{new Date(
																	violation.casePrepOccurredAt,
																).toLocaleDateString()}
																)
															</span>
														)}
													</p>
												)
											})()}
									</div>
									{/* Claim Type */}
									<div style={{ flex: 1 }}>
										<div className='field-label'>Claim Type *</div>
										<select
											className='form-select'
											value={violation.violationType || ''}
											style={
												!violation.violationType
													? { borderColor: '#ef4444' }
													: {}
											}
											onChange={(e) =>
												this.updateViolationField(
													violation.violationId,
													'violationType',
													e.target.value,
												)
											}
										>
											<option value=''>— Select Claim Type —</option>
											<option value='Commercial'>Commercial</option>
											<option value='Editorial'>Editorial</option>
										</select>
									</div>

									{/* Market */}
									<div style={{ flex: 1 }}>
										<div className='field-label'>Market *</div>
										<select
											className='form-select'
											value={violation.market || ''}
											style={
												!violation.market ? { borderColor: '#ef4444' } : {}
											}
											onChange={(e) =>
												this.updateViolationField(
													violation.violationId,
													'market',
													e.target.value,
												)
											}
										>
											<option value=''>— Select Market —</option>
											{markets.map((market) => (
												<option key={market.code} value={market.code}>
													{market.fullName}
												</option>
											))}
										</select>
									</div>

									{/* Allocation Note */}
									<div style={{ flex: 1 }}>
										<div className='field-label'>Allocation Note</div>
										<select
											className='form-select'
											value={violation.allocationNotes || ''}
											onChange={(e) =>
												this.updateViolationField(
													violation.violationId,
													'allocationNotes',
													e.target.value || null,
												)
											}
										>
											<option value=''>— None —</option>
											{ALLOCATION_NOTE_OPTIONS.map((opt) => (
												<option key={opt} value={opt}>
													{opt}
												</option>
											))}
										</select>
									</div>

									{/* Screenshot */}
									<div style={{ flex: 1 }}>
										<div className='field-label'>Screenshot</div>
										<a
											href={`${getApiBase()}/violations/screenshot/600/${violation.violationId}`}
											target='_blank'
											rel='noopener noreferrer'
											className='url-link'
										>
											View Screenshot
										</a>
									</div>

									{/* URL */}
									<div style={{ flex: 2 }}>
										<div className='field-label'>
											Website URL ({violation.domainName})
										</div>
										<a
											href={violation.urlRaw || violation.url}
											target='_blank'
											rel='noopener noreferrer'
											className='url-link'
											title={violation.urlRaw || violation.url}
										>
											{(violation.urlRaw || violation.url || '').length > 50
												? (violation.urlRaw || violation.url).slice(0, 50) +
													'...'
												: violation.urlRaw || violation.url}
										</a>
									</div>
								</div>

								{/* First Date of Publication, Wayback URL - Only for US violations with registered images */}
								{dateOfPublicationAvailable({
									market: violation.market,
									isRegistered: violation.registered,
								}) && (
									<div className='form-row-2' style={{ marginTop: '8px' }}>
										<div className='form-field'>
											<label className='form-label'>
												First Date of Publication
											</label>
											<input
												type='date'
												className='form-input'
												value={
													violation.firstDateOfPublication
														? violation.firstDateOfPublication.substring(0, 10)
														: ''
												}
												onChange={(e) =>
													this.updateViolationField(
														violation.violationId,
														'firstDateOfPublication',
														e.target.value,
													)
												}
											/>
										</div>
										<div className='form-field'>
											<label className='form-label'>Wayback URL</label>
											<input
												type='text'
												className='form-input'
												placeholder='Enter wayback URL'
												style={
													violation.waybackUrl && !isURL(violation.waybackUrl)
														? { borderColor: '#ef4444' }
														: {}
												}
												value={violation.waybackUrl || ''}
												onChange={(e) =>
													this.updateViolationField(
														violation.violationId,
														'waybackUrl',
														e.target.value,
													)
												}
											/>
											{violation.waybackUrl && !isURL(violation.waybackUrl) && (
												<span
													style={{
														color: '#ef4444',
														fontSize: '12px',
														marginTop: '4px',
													}}
												>
													Must be a valid URL
												</span>
											)}
										</div>
									</div>
								)}

								{/* Comments Section */}
								<div className='comments-section' style={{ marginTop: '12px' }}>
									{violation.comment && (
										<div className='comment-item'>
											<div className='comment-label'>Comment</div>
											<div className='comment-text'>{violation.comment}</div>
										</div>
									)}
									<div className='form-field'>
										<label className='form-label'>Internal Comment</label>
										<textarea
											className='form-input'
											rows={3}
											placeholder='Enter internal comment'
											value={violation.internalComment || ''}
											onChange={(e) =>
												this.updateViolationField(
													violation.violationId,
													'internalComment',
													e.target.value,
												)
											}
										/>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Form Fields Grid */}
				<ContactForm
					data={o}
					onFieldChange={this.updateField}
					onArrayFieldChange={this.updateArrayField}
					onAddArrayField={this.addArrayField}
					onRemoveArrayField={this.removeArrayField}
					onPhoneFieldChange={this.updatePhoneField}
					onFormValidChange={this.handleFormValidChange}
					onOrganizationFound={this.handleOrganizationFound}
				/>
				{/* Action Buttons */}
				<div className='save-section'>
					<div
						style={{
							display: 'flex',
							gap: '12px',
							justifyContent: 'flex-end',
						}}
					>
						{/* Save Button */}
						<button
							className={`save-button ${isSaving ? 'saving' : ''} ${
								isSaved ? 'saved' : ''
							}`}
							disabled={isSubmitDisabled}
							onClick={async () => {
								await this.saveContact(o)
								onSave(o)
							}}
							style={{
								background: isSaved ? '#10b981' : '#6b7280',
								cursor: isSaving ? 'not-allowed' : 'pointer',
							}}
						>
							{isSaving ? (
								<>
									<div className='save-spinner'></div>
									Saving...
								</>
							) : isSaved ? (
								<>
									<svg
										className='save-icon'
										viewBox='0 0 20 20'
										fill='currentColor'
									>
										<path
											fillRule='evenodd'
											d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
											clipRule='evenodd'
										/>
									</svg>
									Saved
								</>
							) : (
								<>
									<svg
										className='save-icon'
										viewBox='0 0 20 20'
										fill='currentColor'
									>
										<path d='M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z' />
									</svg>
									Save
								</>
							)}
						</button>

						{/* Submit Button */}
						<button
							className={`save-button ${isSaving ? 'saving' : ''}`}
							disabled={isSubmitDisabled}
							onClick={async () => {
								await this.saveAndExport(o)
								onSubmit(o)
							}}
							style={{
								background: '#10b981',
								cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
							}}
						>
							{isSaving ? (
								<>
									<div className='save-spinner'></div>
									Submitting...
								</>
							) : (
								<>
									<svg
										className='save-icon'
										viewBox='0 0 20 20'
										fill='currentColor'
									>
										<path
											fillRule='evenodd'
											d='M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z'
											clipRule='evenodd'
										/>
									</svg>
									Submit
								</>
							)}
						</button>

						<button
							className={`save-button ${isSaving ? 'saving' : ''}`}
							onClick={async () => {
								await this.saveAndMarkNoContactInfo(o)
								onSubmit(o)
							}}
							style={{
								background: '#f59e0b',
								cursor: 'pointer',
							}}
						>
							{isSaving ? (
								<>
									<div className='save-spinner'></div>
									Marking...
								</>
							) : (
								<>
									<svg
										className='save-icon'
										viewBox='0 0 20 20'
										fill='currentColor'
									>
										<path
											fillRule='evenodd'
											d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
											clipRule='evenodd'
										/>
									</svg>
									No Contact Info
								</>
							)}
						</button>
					</div>
				</div>

				{/* Domain Split Modal */}
				{this.state.showSplitModal && (
					<DigitalPropertySplitModal
						organization={o}
						onClose={() => this.setState({ showSplitModal: false })}
						onSave={this.handleDomainSplitSave}
					/>
				)}
			</div>
		)
	}

	buildOrgPayload = (organization) => {
		const { parsePhoneNumberWithError } = require('libphonenumber-js')

		const emailAddresses = dedupStrings(
			(organization.emailAddresses || []).map((e) => (e || '').toLowerCase()),
		)

		const rawPhoneNumbers = []
		;(organization.phoneNumbers || []).forEach((phone) => {
			if (
				phone.phoneNumber &&
				phone.phoneNumber.trim() &&
				phone.prefix &&
				phone.prefix.trim()
			) {
				try {
					const fullNumber = `${phone.prefix.trim()}${phone.phoneNumber.trim()}`
					const parsed = parsePhoneNumberWithError(fullNumber)
					if (parsed.isValid()) {
						rawPhoneNumbers.push(parsed.format('E.164'))
					}
				} catch (e) {
					console.warn('Failed to parse phone number:', phone, e)
				}
			}
		})
		const phoneNumbers = dedupStrings(rawPhoneNumbers)

		const domains = organization.domains || []
		const allViolationIds = organization.violations.map((v) => v.violationId)

		return [
			{
				organizationId: organization.organizationId || null,
				counterpartyType: organization.counterpartyType,
				companyNumber: organization.companyNumber || null,
				companyName: organization.companyName,
				address: organization.address || null,
				postalCode: organization.postalCode || null,
				city: organization.city || null,
				country: organization.country || null,
				emailAddresses,
				phoneNumbers,
				domains:
					domains.length > 0
						? domains.map((domain) => ({
								domainName: domain.domainName,
								violationIds: organization.violations
									.filter((v) => v.digitalPropertyId === domain.digitalPropertyId)
									.map((v) => v.violationId),
								violations: organization.violations
									.filter((v) => v.digitalPropertyId === domain.digitalPropertyId)
									.map((v) => ({
										violationId: v.violationId,
										violationType: v.violationType,
										allocationNotes: v.allocationNotes || null,
									})),
							}))
						: [
								{
									domainName: organization.companyName || 'unknown',
									violationIds: allViolationIds,
									violations: organization.violations.map((v) => ({
										violationId: v.violationId,
										violationType: v.violationType,
										allocationNotes: v.allocationNotes || null,
									})),
								},
							],
			},
		]
	}

	saveContact = async (organization) => {
		const organizationId = organization.organizationId
		this.setState({ saving: true })
		try {
			await createOrganizations(this.buildOrgPayload(organization))
			await this.saveViolationFields(organization.violations)

			this.setState({
				saving: false,
				saved: true,
			})
			setTimeout(() => this.setState({ saved: false }), 3000)
		} catch (e) {
			console.error('Failed saving organization', organizationId, e)
			this.setState({ saving: false })
			alert('Failed to save organization ' + organizationId)
		}
	}

	updatePhoneField = (index, field, value) => {
		const { organization, onOrganizationChange } = this.props
		const phoneNumbers = [...(organization.phoneNumbers || [])]
		phoneNumbers[index] = { ...phoneNumbers[index], [field]: value }

		if (onOrganizationChange) {
			onOrganizationChange({ ...organization, phoneNumbers })
		}
		this.setState({ saved: false })
	}

	saveAndExport = async (organization) => {
		const organizationId = organization.organizationId
		this.setState({ saving: true })
		try {
			await createOrganizations(this.buildOrgPayload(organization))
			await this.saveViolationFields(organization.violations)

			// Mark all violations in this organization for export
			for (const violation of organization.violations) {
				await markViolationForExport(violation.violationId)
			}

			// Clear saving state
			this.setState({ saving: false })
		} catch (e) {
			console.log(e.message)
			console.error(
				'Failed saving and marking organization for export',
				organizationId,
				e,
			)
			this.setState({ saving: false })
			alert(
				`Failed to save and mark organization for export ${organizationId} ${e.message || ''}`,
			)
		}
	}

	saveAndMarkNoContactInfo = async (organization) => {
		const organizationId = organization.organizationId
		this.setState({ saving: true })
		try {
			// Mark all violations in this organization as no contact information
			for (const violation of organization.violations) {
				await markNoContactInformation(violation.violationId)
			}

			// Clear saving state
			this.setState({ saving: false })
		} catch (e) {
			console.error(
				'Failed marking organization as no contact info',
				organizationId,
				e,
			)
			this.setState({ saving: false })
			alert('Failed to mark organization as no contact info ' + organizationId)
		}
	}

	updateField = (field, value) => {
		const { organization, onOrganizationChange } = this.props
		if (onOrganizationChange) {
			onOrganizationChange({ ...organization, [field]: value })
		}
		this.setState({ saved: false })
	}

	updateArrayField = (field, index, value) => {
		const { organization, onOrganizationChange } = this.props
		const array = [...(organization[field] || [])]
		array[index] = value

		if (onOrganizationChange) {
			onOrganizationChange({ ...organization, [field]: array })
		}
		this.setState({ saved: false })
	}

	getCallingCode = (countryCode) => {
		const market = markets.find((m) => m.code === countryCode)
		return market ? market.phoneNumberCode : ''
	}

	addArrayField = (field) => {
		const { organization, onOrganizationChange } = this.props
		let newValue = ''
		if (field === 'phoneNumbers') {
			const callingCode = this.getCallingCode(organization.country)
			newValue = { prefix: callingCode || '', phoneNumber: '' }
		}
		const array = [...(organization[field] || []), newValue]
		if (onOrganizationChange) {
			onOrganizationChange({ ...organization, [field]: array })
		}
		this.setState({ saved: false })
	}

	removeArrayField = (field, index) => {
		const { organization, onOrganizationChange } = this.props
		const array = [...(organization[field] || [])]
		array.splice(index, 1)
		if (onOrganizationChange) {
			onOrganizationChange({ ...organization, [field]: array })
		}
		this.setState({ saved: false })
	}
}

DigitalPropertyContactEnrichmentCard.propTypes = {
	organization: PropTypes.object.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onOrganizationChange: PropTypes.func,
	index: PropTypes.number,
}

export default DigitalPropertyContactEnrichmentCard
